import React, { useState } from 'react';
import { Box, TextField, Button, Paper, Typography, Alert, Link, Grid, Stack, Chip } from '@mui/material';
import { School, ArrowForward, CheckCircleOutline, VerifiedUser } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../api';

const SchoolSignup: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    schoolSecretId: '',
    confirmSchoolSecretId: '',
    address: '',
    city: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async () => {
    setError(null);
    if (
      !formData.name ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.schoolSecretId ||
      !formData.confirmSchoolSecretId
    ) {
      setError('Please fill in all required fields.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (formData.schoolSecretId !== formData.confirmSchoolSecretId) {
      setError('School Secret IDs do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/schools/register', {
        ...formData,
        email: formData.email.trim().toLowerCase(),
      });
      setSuccess(true);
      setTimeout(() => navigate('/school/login'), 2000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '100vh', alignItems: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #dbeafe 100%)', px: 2 }}>
        <Paper sx={{ p: 4, width: 420, textAlign: 'center', borderRadius: 4, boxShadow: 6 }}>
          <Typography variant="h5" sx={{ mb: 2, color: 'success.main', fontWeight: 700 }}>
            Registration Successful!
          </Typography>
          <Typography>
            Your school has been registered. Redirecting to login...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f5f3ff 100%)', px: 2, py: 6 }}>
      <Grid container spacing={4} sx={{ maxWidth: 1180, width: '100%', alignItems: 'center' }}>
        <Grid item xs={12} md={5}>
          <Box sx={{ p: { xs: 0, md: 2 } }}>
            <Chip icon={<VerifiedUser />} label="School Registration" color="primary" sx={{ mb: 2, fontWeight: 600 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: '#0f172a' }}>
              Build a smarter school operations system
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.8, mb: 3 }}>
              Create your school profile and start managing academic records, staff, student data, assessments, and secure communication from one trusted portal.
            </Typography>
            <Stack spacing={2}>
              {[
                'Seamless student and staff onboarding',
                'Academic and term result tracking',
                'Transparent payments and parent communication'
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleOutline color="success" />
                  <Typography color="text.secondary">{item}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper elevation={6} sx={{ p: 4, borderRadius: 4, maxWidth: 620, mx: 'auto', border: '1px solid rgba(148,163,184,0.22)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '50%', background: 'linear-gradient(135deg, #0ea5e9, #4f46e5)', color: '#fff', display: 'flex' }}>
                <School />
              </Box>
            </Box>
            <Typography variant="h4" sx={{ mb: 1, textAlign: 'center', fontWeight: 700, color: '#0f172a' }}>
              Register Your School
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
              Set up your institution to unlock secure school administration tools.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              <TextField label="School Name" name="name" fullWidth value={formData.name} onChange={handleChange} required />
              <TextField label="Email" name="email" type="email" fullWidth value={formData.email} onChange={handleChange} required />
              <TextField label="Password" name="password" type="password" fullWidth value={formData.password} onChange={handleChange} required />
              <TextField label="Confirm Password" name="confirmPassword" type="password" fullWidth value={formData.confirmPassword} onChange={handleChange} required />
              <TextField label="School Secret ID" name="schoolSecretId" fullWidth value={formData.schoolSecretId} onChange={handleChange} required />
              <TextField label="Confirm Secret ID" name="confirmSchoolSecretId" fullWidth value={formData.confirmSchoolSecretId} onChange={handleChange} required />
              <TextField label="Address" name="address" fullWidth value={formData.address} onChange={handleChange} sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }} />
              <TextField label="City" name="city" fullWidth value={formData.city} onChange={handleChange} />
              <TextField label="Country" name="country" fullWidth value={formData.country} onChange={handleChange} />
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={loading}
              endIcon={<ArrowForward />}
              sx={{ mt: 3, py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 2 }}
            >
              {loading ? 'Registering...' : 'Register School'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link component={RouterLink} to="/school/login" sx={{ textDecoration: 'none', fontWeight: 600 }}>
                Already have an account? Sign in
              </Link>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
              All rights reserved. © {new Date().getFullYear()} EduTech Hub.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SchoolSignup;